import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useSession } from '../../context/SessionContext';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useAuth } from '../../context/AuthContext';

const ProtectedRoute = ({ children, sessionRequired = true, adminRequired = false, allowWithoutOnboarding = false }) => {
  const { isAuthenticated, isAdmin, user, loading: authLoading } = useAuth();
  const sessionContext = useSession();
  const { session, participant, initializing } = sessionContext;
  const location = useLocation();
  const { sessionId: urlSessionId } = useParams();

  const [isCheckingSession, setIsCheckingSession] = useState(sessionRequired);

  useEffect(() => {
    if (!sessionRequired) {
      setIsCheckingSession(false);
      return;
    }

    if (initializing) {
      setIsCheckingSession(true);
      return;
    }

    setIsCheckingSession(false); // Assume check is complete for now

  }, [sessionRequired, session, participant, urlSessionId, initializing, location.pathname]);

  if (authLoading || (sessionRequired && (initializing || isCheckingSession))) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Authentication Check (applies to all protected routes)
  if (!isAuthenticated) {
    console.log(`[ProtectedRoute] NOT AUTHENTICATED. Path: ${location.pathname}. Redirecting to /login.`);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Onboarding gate: a brand-new user (null onboardingCompletedAt) is hard-gated
  // into the first-time flow until they create their first org and pick a
  // methodology. The onboarding route itself opts out via allowWithoutOnboarding
  // so it can render for exactly these users.
  if (!allowWithoutOnboarding && isAuthenticated && user && !user.onboardingCompletedAt) {
    console.log(`[ProtectedRoute] Onboarding incomplete. Path: ${location.pathname}. Redirecting to /onboarding.`);
    return <Navigate to="/onboarding" replace />;
  }

  // Admin Check (only if adminRequired is true)
  if (adminRequired && !isAdmin) {
    console.log(`[ProtectedRoute] ADMIN ACCESS REQUIRED but user NOT ADMIN. Path: ${location.pathname}. Redirecting to /dashboard.`);
    return <Navigate to="/dashboard" state={{ from: location }} replace />;
  }

  // Handle routes that require authentication but NOT a session (e.g., /dashboard, /create, /admin)
  if (!sessionRequired) {
    return children;
  }

  if (isAuthenticated) {
    return children;
  }
  
  // Fallback for unauthenticated users trying to access a session route (e.g. guests joining via link)
  if (session && participant && session.sessionId === urlSessionId) {
    return children; 
  }
  
  console.log(`[ProtectedRoute] Path: ${location.pathname} - Not authenticated and no valid session context for ${urlSessionId}. Redirecting to /join/${urlSessionId}.`);
  return <Navigate to={`/join/${urlSessionId}?from=${location.pathname}`} replace />;

};

export default ProtectedRoute; 